import { Link } from "react-router-dom";
import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    // Ensure the page starts at the top when the component mounts
    // Wrap in a try/catch to avoid errors in non-browser environments (e.g., SSR)
    try {
      window.scrollTo(0, 0);
    } catch (e) {
      console.warn("Unable to scroll to top:", e);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="w-full px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/fe226e3a566555ddeb1e5a20b9b4643c/5ff4ff3d5009244f40339f86dca03486.jpeg"
                alt="FluxPoint Digital LLC"
                className="h-12 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-[#1A2B4A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-24 px-8 lg:px-16">
        <article className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-[#1A2B4A] mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-10">
            Effective Date: January 1, 2025
          </p>

          <div className="space-y-8 text-[15px] leading-relaxed text-gray-700">
            <p>
              FluxPoint Digital LLC (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) respects your privacy and is committed to protecting your information.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, and safeguard information when you visit our website.
            </p>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#info-collect" id="info-collect">1. Information We Collect</a>
              </h4>
              <p className="mb-3">We may collect:</p>

              <p className="font-medium text-[#1A2B4A] mb-2">a. Personal Information</p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Information submitted through contact forms</li>
              </ul>

              <p className="font-medium text-[#1A2B4A] mb-2">b. Automatically Collected Information</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Website usage data through analytics tools</li>
              </ul>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#how-use" id="how-use">2. How We Use Information</a>
              </h4>
              <p className="mb-3">We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>Respond to inquiries</li>
                <li>Provide services and consultations</li>
                <li>Improve our website and user experience</li>
                <li>Maintain security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p><strong>We do not sell personal information.</strong></p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#cookies" id="cookies">3. Cookies &amp; Tracking</a>
              </h4>
              <p className="mb-2">
                We may use cookies or analytics tools to understand website usage and improve performance.
              </p>
              <p>You may disable cookies through your browser settings.</p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#data-sharing" id="data-sharing">4. Data Sharing</a>
              </h4>
              <p className="mb-3">We may share information only:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>With service providers assisting in website operation</li>
                <li>When required by law</li>
                <li>To protect rights, property, or safety</li>
              </ul>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#data-security" id="data-security">5. Data Security</a>
              </h4>
              <p>
                We implement reasonable security measures to protect information. However, no online transmission is completely secure.
              </p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#your-rights" id="your-rights">6. Your Rights</a>
              </h4>
              <p className="mb-3">Depending on your jurisdiction, you may have rights to:</p>
              <ul className="list-disc pl-6 space-y-1 mb-4">
                <li>Request access to your data</li>
                <li>Request deletion</li>
                <li>Request correction</li>
              </ul>
              <p>
                To make a request, contact:{" "}
                <a
                  href="mailto:admin@fluxpointdigital.org"
                  className="text-[#1A2B4A] font-medium underline hover:text-[#2a3f66] cursor-pointer"
                >
                  admin@fluxpointdigital.org
                </a>
              </p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#children" id="children">7. Children&apos;s Privacy</a>
              </h4>
              <p>
                Our website is not directed toward individuals under 13. We do not knowingly collect information from children.
              </p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#changes" id="changes">8. Changes to This Policy</a>
              </h4>
              <p>
                We may update this Privacy Policy periodically. Updates will be reflected by the effective date above.
              </p>
            </section>

            <section>
              <h4 className="text-lg font-semibold text-[#1A2B4A] mb-3">
                <a href="#contact-info" id="contact-info">9. Contact Information</a>
              </h4>
              <p>FluxPoint Digital LLC</p>
              <p>United States</p>
              <p>
                <a
                  href="mailto:admin@fluxpointdigital.org"
                  className="text-[#1A2B4A] font-medium underline hover:text-[#2a3f66] cursor-pointer"
                >
                  admin@fluxpointdigital.org
                </a>
              </p>
            </section>
          </div>
        </article>
      </main>

      <footer className="bg-[#2C2C2C] text-white">
        <div className="border-t border-gray-700">
          <div className="max-w-7xl mx-auto px-8 lg:px-16 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <p className="text-xs text-gray-500">
                  © 2026 FluxPoint Digital LLC. All rights reserved.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Registered in California, USA.
                </p>
              </div>
              <div className="flex space-x-6">
                <Link
                  to="/"
                  className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  Home
                </Link>
                <Link
                  to="/terms-of-service"
                  className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  Terms of Service
                </Link>
                <a
                  href="https://readdy.ai/?ref=logo"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
